import { useEffect, useState } from 'react';
import StatCard from '../components/StatCard';
import { supabase } from '../lib/supabase';
import { ARCHETYPE_COLORS, Profile } from '../types';

interface DashStats {
  totalAthletes: number;
  totalUsers: number;
  totalInstitutions: number;
  activeInstitutions: number;
  pendingInstitutions: number;
  expiredInstitutions: number;
  completedAssessments: number;
  waitlistCount: number;
}

interface ArchetypeCount { name: string; count: number; }
interface RecentAthlete extends Pick<Profile, 'id' | 'full_name' | 'username' | 'sport' | 'athlete_archetype' | 'assessment_status' | 'updated_at'> {}

function initials(name: string | null): string {
  if (!name) return '?';
  return name.split(' ').map(p => p[0]).join('').toUpperCase().slice(0, 2);
}

function timeAgo(ts: string | null): string {
  if (!ts) return 'Unknown';
  const diff = Date.now() - new Date(ts).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(ts).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' });
}

export default function DashboardPage() {
  const [stats, setStats] = useState<DashStats>({
    totalAthletes: 0, totalUsers: 0,
    totalInstitutions: 0, activeInstitutions: 0, pendingInstitutions: 0, expiredInstitutions: 0,
    completedAssessments: 0, waitlistCount: 0,
  });
  const [archetypes, setArchetypes] = useState<ArchetypeCount[]>([]);
  const [recent, setRecent] = useState<RecentAthlete[]>([]);
  const [unassessedCount, setUnassessedCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      const [
        { count: totalUsers },
        { count: totalAthletes },
        { count: completedAssessments },
        { count: unassessed },
        { count: waitlistCount },
        { count: activeInstitutions },
        { count: pendingInstitutions },
        { count: expiredInstitutions },
        { count: totalInstitutions },
        { data: profilesRaw },
        { data: recentRaw },
      ] = await Promise.all([
        // All platform users
        supabase.from('profiles').select('*', { count: 'exact', head: true }),
        // Athletes only — exclude admins and internal staff
        supabase.from('profiles').select('*', { count: 'exact', head: true })
          .eq('is_admin', false)
          .eq('user_type', 'athlete'),
        // Completed assessments among athletes only
        supabase.from('profiles').select('*', { count: 'exact', head: true })
          .eq('is_admin', false)
          .eq('user_type', 'athlete')
          .eq('assessment_status', 'completed'),
        // Unassessed athletes
        supabase.from('profiles').select('*', { count: 'exact', head: true })
          .eq('is_admin', false)
          .eq('user_type', 'athlete')
          .or('assessment_status.eq.not_started,assessment_status.is.null'),
        // Waitlist
        supabase.from('waitlist').select('*', { count: 'exact', head: true }),
        // Institution breakdowns
        supabase.from('institutions').select('*', { count: 'exact', head: true }).eq('status', 'active'),
        supabase.from('institutions').select('*', { count: 'exact', head: true }).eq('status', 'pending'),
        supabase.from('institutions').select('*', { count: 'exact', head: true }).in('status', ['expired', 'suspended']),
        supabase.from('institutions').select('*', { count: 'exact', head: true }),
        // Archetype distribution — athletes only
        supabase.from('profiles').select('athlete_archetype')
          .eq('is_admin', false)
          .eq('user_type', 'athlete')
          .not('athlete_archetype', 'is', null),
        // Recent athlete activity
        supabase.from('profiles').select('id,full_name,username,sport,athlete_archetype,assessment_status,updated_at')
          .eq('is_admin', false)
          .eq('user_type', 'athlete')
          .order('updated_at', { ascending: false })
          .limit(10),
      ]);

      setStats({
        totalUsers: totalUsers ?? 0,
        totalAthletes: totalAthletes ?? 0,
        completedAssessments: completedAssessments ?? 0,
        waitlistCount: waitlistCount ?? 0,
        totalInstitutions: totalInstitutions ?? 0,
        activeInstitutions: activeInstitutions ?? 0,
        pendingInstitutions: pendingInstitutions ?? 0,
        expiredInstitutions: expiredInstitutions ?? 0,
      });
      setUnassessedCount(unassessed ?? 0);
      setRecent((recentRaw as RecentAthlete[]) ?? []);

      // Archetype distribution — athletes only
      const counts: Record<string, number> = {};
      (profilesRaw ?? []).forEach((p: any) => {
        const a = p.athlete_archetype || 'Unassessed';
        counts[a] = (counts[a] || 0) + 1;
      });
      const order = ['The Commander', 'The Architect', 'The Catalyst', 'Unassessed'];
      const sorted = order.map(name => ({ name, count: counts[name] || 0 })).filter(a => a.count > 0);
      setArchetypes(sorted.length ? sorted : []);

      setLoading(false);
    }
    load();
  }, []);

  const completionPct = stats.totalAthletes > 0
    ? Math.round((stats.completedAssessments / stats.totalAthletes) * 100)
    : 0;

  const archetypeTotal = archetypes.reduce((s, a) => s + a.count, 0) || 1;

  const institutionDelta = [
    stats.activeInstitutions > 0 ? `${stats.activeInstitutions} active` : '',
    stats.pendingInstitutions > 0 ? `${stats.pendingInstitutions} pending` : '',
    stats.expiredInstitutions > 0 ? `${stats.expiredInstitutions} expired` : '',
  ].filter(Boolean).join(' · ') || 'No institutions yet';

  return (
    <div>
      {/* Alert strip */}
      {unassessedCount > 0 && (
        <div className="alert-strip">
          <div style={{ width: 30, height: 30, borderRadius: 7, background: '#bf1e2e', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, flexShrink: 0 }}>⚠</div>
          <div style={{ fontSize: 13, color: 'var(--text)', flex: 1 }}>
            <span style={{ fontWeight: 700, color: '#bf1e2e' }}>{unassessedCount} athlete{unassessedCount !== 1 ? 's' : ''}</span>{' '}
            {unassessedCount === 1 ? 'has' : 'have'} not completed their Identity Assessment — their Transition Pathway is locked.
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid-4 mb-22">
        <StatCard
          label="Institutions"
          value={loading ? '—' : stats.totalInstitutions}
          delta={loading ? '...' : institutionDelta}
          deltaUp={stats.activeInstitutions > 0}
          color="#0fa86d"
          icon="⬡"
        />
        <StatCard
          label="Total Athletes"
          value={loading ? '—' : stats.totalAthletes}
          delta="Registered athletes"
          deltaUp={true}
          color="#014c8d"
          icon="◈"
        />
        <StatCard
          label="Platform Users"
          value={loading ? '—' : stats.totalUsers}
          delta="All roles (app + web)"
          deltaUp={true}
          color="#6b46c1"
          icon="◉"
        />
        <StatCard
          label="Waitlist"
          value={loading ? '—' : stats.waitlistCount}
          delta="Signups"
          deltaUp={true}
          color="#e88c0e"
          icon="▤"
        />
      </div>

      {/* Archetype breakdown + Recent activity */}
      <div className="grid-2 mb-18">

        {/* Archetype breakdown */}
        <div className="panel">
          <div className="panel-header">
            <div className="panel-title">Archetype Distribution</div>
            <span style={{ fontSize: 11, color: 'var(--text-3)' }}>{stats.completedAssessments} assessed</span>
          </div>
          <div style={{ padding: '16px 18px' }}>
            {loading ? (
              <div style={{ color: 'var(--text-3)', fontSize: 13, padding: '20px 0', textAlign: 'center' }}>Loading...</div>
            ) : archetypes.length === 0 ? (
              <div style={{ color: 'var(--text-3)', fontSize: 13, padding: '20px 0', textAlign: 'center' }}>No assessments completed yet</div>
            ) : (
              archetypes.map((a) => {
                const color = ARCHETYPE_COLORS[a.name] || '#4a6080';
                const pct = Math.round((a.count / archetypeTotal) * 100);
                return (
                  <div key={a.name} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)', width: 110, flexShrink: 0 }}>{a.name}</div>
                    <div style={{ flex: 1, height: 7, background: 'var(--border)', borderRadius: 10, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 10, transition: 'width 0.4s' }} />
                    </div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-2)', width: 28, textAlign: 'right' }}>{a.count}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-3)', width: 32, textAlign: 'right' }}>{pct}%</div>
                  </div>
                );
              })
            )}
            {!loading && stats.totalAthletes > 0 && (
              <div style={{ marginTop: 8, padding: '8px 10px', borderRadius: 8, background: 'rgba(1,76,141,0.08)', border: '1px solid rgba(1,76,141,0.15)', fontSize: 11, color: 'var(--text-2)', textAlign: 'center' }}>
                {stats.completedAssessments} of {stats.totalAthletes} athletes assessed · {completionPct}% completion
              </div>
            )}
          </div>
        </div>

        {/* Recent activity */}
        <div className="panel">
          <div className="panel-header">
            <div className="panel-title">Recent Athletes</div>
            <span style={{ fontSize: 11, color: 'var(--text-3)' }}>Last active</span>
          </div>
          <div style={{ padding: '8px 0' }}>
            {loading ? (
              <div style={{ color: 'var(--text-3)', fontSize: 13, padding: '24px', textAlign: 'center' }}>Loading...</div>
            ) : recent.length === 0 ? (
              <div style={{ color: 'var(--text-3)', fontSize: 13, padding: '24px', textAlign: 'center' }}>No athletes yet</div>
            ) : (
              recent.map((p) => {
                const archetypeColor = p.athlete_archetype ? (ARCHETYPE_COLORS[p.athlete_archetype] || '#4a6080') : '#4a6080';
                const isComplete = p.assessment_status === 'completed';
                return (
                  <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 18px', borderBottom: '1px solid var(--border)' }}>
                    <div style={{ width: 30, height: 30, borderRadius: '50%', background: archetypeColor, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 11, color: '#fff', flexShrink: 0 }}>
                      {initials(p.full_name)}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {p.full_name || p.username || 'Anonymous'}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--text-3)' }}>
                        {p.sport || 'Sport not set'} · {isComplete ? (p.athlete_archetype || 'Assessed') : 'Not assessed'}
                      </div>
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-3)', flexShrink: 0, textAlign: 'right' }}>
                      <div>{timeAgo(p.updated_at)}</div>
                      <div style={{ color: isComplete ? '#0fa86d' : '#e88c0e', fontWeight: 600, fontSize: 10, marginTop: 2 }}>
                        {isComplete ? '✓ Assessed' : '○ Pending'}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

      </div>

      {/* Assessment funnel — athletes only */}
      {!loading && stats.totalAthletes > 0 && (
        <div className="panel">
          <div className="panel-header">
            <div className="panel-title">Assessment Funnel</div>
            <span style={{ fontSize: 11, color: 'var(--text-3)' }}>Athletes only</span>
          </div>
          <div style={{ padding: '18px 20px', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
            {[
              { label: 'Total Athletes', value: stats.totalAthletes, color: '#014c8d', pct: 100 },
              { label: 'Assessment Started', value: stats.totalAthletes - unassessedCount, color: '#e88c0e', pct: stats.totalAthletes > 0 ? Math.round(((stats.totalAthletes - unassessedCount) / stats.totalAthletes) * 100) : 0 },
              { label: 'Assessment Completed', value: stats.completedAssessments, color: '#0fa86d', pct: completionPct },
            ].map((stage) => (
              <div key={stage.label} style={{ background: 'var(--surface-2)', borderRadius: 10, padding: '16px', border: '1px solid var(--border)' }}>
                <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', color: 'var(--text-3)', marginBottom: 8 }}>{stage.label}</div>
                <div style={{ fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 800, fontSize: 36, color: 'var(--text)', lineHeight: 1 }}>{stage.value}</div>
                <div style={{ fontSize: 11, color: stage.color, fontWeight: 700, marginTop: 4, marginBottom: 10 }}>{stage.pct}%</div>
                <div style={{ height: 5, background: 'var(--border)', borderRadius: 10, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${stage.pct}%`, background: stage.color, borderRadius: 10, transition: 'width 0.4s' }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
